import { useEffect, useState, useContext, useRef } from 'react';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { Modal, Button } from 'react-bootstrap';
import Select from "../../AppSelect";
import Swal from 'sweetalert2';

import { useAuth } from '../../../hooks/useAuth';
import { PermissionContext } from '../../../hooks/PermissionContext';

import { validateCSVFile, ValidMimeTypes } from '../../../utils/files.utils';
import { customStyles } from '../../../constants/customSelectStyles';

const UploadAddressFile = ({
    show,
    shippers,
    handleClose,
    reduxUploadInsertionOrdersFile,
    ordersInsertionFileUploaded,
    reduxResetInsertionOrdersFile,
}) => {
    const { session } = useAuth();
    const { hasPermission } = useContext(PermissionContext);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
        control,
    } = useForm({
        defaultValues: {
            shipper: null,
            file: null,
        }
    });

    const fileWatcher = useRef({});
    fileWatcher.current = watch('file', '');

    const [fileName, setFileName] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        if (!show) return;

        if (hasPermission("feature:isClient") && session?.role?.company) {
            const shipper = shippers.items.find((s) => s.idCompany === session?.role?.company.idCompany);

            setIsClient(true);

            setValue('shipper', {
                label: shipper.description,
                value: shipper.idCompany
            });
        }
    }, [show, shippers, setValue, session]);

    useEffect(() => {
        if (!show) return;

        if (fileWatcher.current != null && fileWatcher.current.length > 0) {
            setFileName(fileWatcher.current[0].name);
        }
    }, [show, fileWatcher.current]);

    useEffect(() => {
        if (!show) return;

        if (ordersInsertionFileUploaded) {
            let processCompletedMessage = `<p>Se han creado ${ordersInsertionFileUploaded.processed} órdenes de ${ordersInsertionFileUploaded.total}.</p>`;

            let hasIssues = false;
            if (ordersInsertionFileUploaded.invalidItems.length > 0) {
                processCompletedMessage += `<b style="color: red;">Advertencia</b> <p>Se encontraron órdenes con problemas, por favor revise el archivo descargado con los detalles.</p>`;
                hasIssues = true;
            }

            Swal.fire({
                icon: 'info',
                title: 'Proceso de carga completado.',
                html: processCompletedMessage,
                allowEscapeKey: false,
                allowOutsideClick: false,
                showConfirmButton: true,
            }).then((res) => {
                if (res.isConfirmed && !hasIssues) handleCloseModal(true);
            });
        }
    }, [show, ordersInsertionFileUploaded]);

    const handleButtonClick = () => {
        const csvData = `*Pais;*Departamento;*Ciudad;Codigo Postal;Barrio;*Direccion;*Nombre;*Apellido;*Telefono;Comentarios;*Valor Declarado;*Valor a recaudar;*Productos;*Id Externo;*Esta Columna es obligatoria; **Recuerde eliminar esta linea`
        const fileName = 'templateLoadAddress.csv';
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleCloseModal = (reloadTable = false) => {
        reset();
        setFileName(null);
        setFileError(null);
        handleClose(reloadTable);
        reduxResetInsertionOrdersFile();
    }

    const onSubmitUpload = async (data) => {
        const file = data.file[0];

        const isValid = await validateCSVFile(file, 22, 26);

        if (!isValid) return;

        setTimeout(() => {
            reduxUploadInsertionOrdersFile({
                file: file,
                idShipper: data.shipper.value,
            });
        }, 2000);
    };

    return (
        <Modal
            show={show}
            onHide={handleCloseModal}
            onEscapeKeyDown={handleCloseModal}
            backdrop='static'
            keyboard={true}>
            <form onSubmit={handleSubmit(onSubmitUpload)}>
                <Modal.Header closeButton>
                    <Modal.Title>Cargar archivo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='row'>
                        <div className='form-group col-md-12' style={{ display: isClient ? 'none' : 'block' }}>
                            <label
                                htmlFor='shipper'
                                className='form-label'>
                                Remitente
                            </label>
                            <Controller
                                name="shipper"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        className={`${errors.shipper && 'is-invalid'}`}
                                        id="shipper"
                                        isClearable={!!isClient}
                                        isDisabled={isClient}
                                        onChange={(value) => {
                                            field.onChange(value);
                                        }}
                                        options={
                                            shippers && Object.keys(shippers).length > 0
                                                ? shippers.items
                                                    .filter((f) => f.isActive && f.createOrderEnabled)
                                                    .map((e, key) => ({
                                                        value: e.idCompany,
                                                        label: e.description,
                                                    }))
                                                : []
                                        }
                                        styles={customStyles}
                                    />
                                )}
                            />
                            {errors.shipper && (
                                <span className="error-message">
                                    {errors.shipper.message}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="row">
                        <div className="form-group col-md-12">
                            <label htmlFor="file" className="form-label">
                                Archivo
                            </label>
                            <div className="custom-file">
                                <input
                                    className={`custom-file-input ${errors.file && 'is-invalid'}`}
                                    type='file'
                                    id='file'
                                    name='file'
                                    accept={ValidMimeTypes.CSV.join(',')}
                                    {...register('file', { required: true })}
                                />{' '}
                                <label
                                    className='custom-file-label'
                                    htmlFor='validatedCustomFile'>
                                    {fileError ? fileError : fileName ? fileName : 'Seleccionar archivo...'}
                                </label>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant='secondary'
                        onClick={handleCloseModal}>
                        Cancelar
                    </Button>
                    <Button
                        variant='primary'
                        type='submit'>
                        Cargar
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    )
}

const mapStateToProps = (state) => {
    return {
        ordersInsertionFileUploaded: state.orderState.ordersInsertionFileUploaded,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        reduxUploadInsertionOrdersFile: (payload) =>
            dispatch({
                type: 'UPLOAD_INSERTION_ORDERS_FILE_REQUEST',
                value: payload,
            }),
        reduxResetInsertionOrdersFile: () =>
            dispatch({
                type: 'RESET_UPLOAD_INSERTION_ORDERS_FILE',
            }),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadAddressFile);
